import React, { useState, useEffect } from 'react';
import { Outlet, useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_BASE_URL } from '../../config';
import LMSSidebar from './LMSSidebar';

export default function LMSLayout() {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const { instituteId } = useParams();
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const roleDisplay = user?.role?.toLowerCase() === 'student' ? 'Student' : 
                      user?.role?.toLowerCase() === 'teacher' ? 'Teacher' : 'Institute';

  const [managedTenants, setManagedTenants] = useState([]);

  useEffect(() => {
    if (user?.role === 'super_tenant') {
      axios.get(`${API_BASE_URL}/api/supertenant/tenants?superTenantEmail=${user.email}`)
        .then(res => {
          setManagedTenants(res.data || []);
        })
        .catch(err => {
          console.error("Error fetching managed tenants", err);
        });
    }
  }, [user?.role, user?.email]);

  const handleTenantChange = (tenantEmail) => {
    const updatedUser = { ...user };
    if (tenantEmail === 'hub') {
      delete updatedUser.impersonatedTenantEmail;
      delete updatedUser.impersonatedSchoolName;
      delete updatedUser.impersonatedLogoUrl;
      localStorage.setItem('user', JSON.stringify(updatedUser));
      window.location.href = `/${user.tenantEmail}/lms/pensense-dashboard`;
    } else {
      const selectedTenant = managedTenants.find(t => t.tenantId === tenantEmail);
      updatedUser.impersonatedTenantEmail = tenantEmail;
      updatedUser.impersonatedSchoolName = selectedTenant?.schoolName || selectedTenant?.adminName;
      updatedUser.impersonatedLogoUrl = selectedTenant?.logoUrl || '';
      localStorage.setItem('user', JSON.stringify(updatedUser));
      window.location.href = `/${tenantEmail}/lms/pensense-dashboard`;
    }
  };

  // Auth guard — redirect to login if no valid session
  useEffect(() => {
    const storedUser = JSON.parse(localStorage.getItem('user') || 'null');
    if (!storedUser?.email || !storedUser?.role) {
      navigate('/', { replace: true });
    }
  }, [navigate]);

  // PWA Install Prompt Capture
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showIOSInstructions, setShowIOSInstructions] = useState(false);

  // Detect iOS/Safari
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
  const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
  const isStandalone = window.matchMedia('(display-mode: standalone)').matches;
  const showIOSInstallButton = isIOS && isSafari && !isStandalone;

  useEffect(() => {
    const handleInstallAvailable = () => {
      if (window.deferredPrompt) {
        setDeferredPrompt(window.deferredPrompt);
      }
    };

    // Check if it's already available (fired before mount)
    if (window.deferredPrompt) {
      setDeferredPrompt(window.deferredPrompt);
    }

    window.addEventListener('pwa-install-available', handleInstallAvailable);
    return () => window.removeEventListener('pwa-install-available', handleInstallAvailable);
  }, []);

  const handleInstallClick = async () => {
    // Check if we're already in standalone mode
    if (isStandalone) {
       alert("The app is already installed and running as a standalone application!");
       return;
    }

    // iOS specific handling
    if (isIOS && isSafari) {
      setShowIOSInstructions(true);
      setShowProfileMenu(false);
      return;
    }

    if (!deferredPrompt) {
      console.log("No deferredPrompt found. Browser might still be validating PWA criteria.");
      alert("The 'Install' prompt is being prepared by your browser. \n\nIf this doesn't pop up in a few seconds, you can always install manually by clicking your browser's menu (three dots) and selecting 'Install App' or 'Add to Home Screen'.");
      return;
    }
    
    try {
      // Show the install prompt
      await deferredPrompt.prompt();
      
      // Wait for the user to respond to the prompt
      const { outcome } = await deferredPrompt.userChoice;
      console.log(`User response to the install prompt: ${outcome}`);
      
      if (outcome === 'accepted') {
        // We've used the prompt, and can't use it again
        setDeferredPrompt(null);
        window.deferredPrompt = null;
      }
    } catch (err) {
      console.error("Installation prompt failed:", err);
    }
    
    setShowProfileMenu(false);
  };

  const handleLogout = () => {
    localStorage.clear();
    window.location.href = '/';
  };

  return (
    <div className="flex min-h-screen bg-slate-950 text-slate-100 font-sans">
      <LMSSidebar 
        isOpen={isSidebarOpen} 
        setIsOpen={setIsSidebarOpen} 
        isCollapsed={isSidebarCollapsed}
        setIsCollapsed={setIsSidebarCollapsed}
        deferredPrompt={deferredPrompt || (showIOSInstallButton ? true : null)}
        onInstallClick={handleInstallClick}
      />
      
      <div className={`flex-1 flex flex-col min-w-0 transition-all duration-300 ${isSidebarCollapsed ? 'sm:ml-20' : 'sm:ml-64'}`}>
        {/* Mobile Header */}
        <header className="sm:hidden flex flex-col p-4 border-b border-slate-800 bg-slate-950/90 backdrop-blur sticky top-0 z-40 gap-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button onClick={() => setIsSidebarOpen(true)} className="text-slate-300 hover:text-white">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 6h16M4 12h16M4 18h16"/></svg>
              </button>
              <span className="font-semibold text-sm tracking-widest text-slate-100 uppercase">LMS CORE</span>
            </div>
            
            <div className="relative">
              <button
                onClick={() => setShowProfileMenu(v => !v)}
                className="h-8 w-8 rounded-full bg-gradient-to-br from-indigo-500 to-purple-500 border border-slate-700 flex items-center justify-center text-white font-bold text-xs focus:outline-none hover:opacity-90 transition-opacity"
              >
                {(user?.name || user?.email || 'U').charAt(0).toUpperCase()}
              </button>

              {showProfileMenu && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setShowProfileMenu(false)} />
                  <div className="absolute right-0 top-10 z-50 w-52 bg-slate-900 border border-slate-700 rounded-xl shadow-2xl py-1 animate-in fade-in slide-in-from-top-2 duration-150">
                    <div className="px-4 py-2.5 border-b border-slate-800">
                      <p className="text-xs font-semibold text-slate-200 truncate">{user?.name || 'User'}</p>
                      <p className="text-xs text-slate-500 truncate">{user?.email}</p>
                    </div>
                    {user?.role?.toLowerCase() === 'student' && (
                      <button
                        onClick={() => { setShowProfileMenu(false); navigate(`/${instituteId}/lms/profile`); }}
                        className="flex w-full items-center gap-3 px-4 py-2.5 text-sm text-slate-300 hover:bg-slate-800 hover:text-white transition-colors"
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path><circle cx="12" cy="7" r="4"></circle></svg>
                        Edit Profile
                      </button>
                    )}
                    {(deferredPrompt || showIOSInstallButton) && (
                      <button
                        onClick={handleInstallClick}
                        className="flex w-full items-center gap-3 px-4 py-2.5 text-sm text-sky-400 hover:bg-sky-500/10 hover:text-sky-300 transition-colors font-semibold"
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                        Install App
                      </button>
                    )}
                    <button
                      onClick={() => { setShowProfileMenu(false); navigate(`/${instituteId}/lms/pensense-dashboard?changePassword=true`); }}
                      className="flex w-full items-center gap-3 px-4 py-2.5 text-sm text-slate-300 hover:bg-slate-800 hover:text-white transition-colors"
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect><path d="M7 11V7a5 5 0 0 1 10 0v4"></path></svg>
                      Change Password
                    </button>
                    <button
                      onClick={handleLogout}
                      className="flex w-full items-center gap-3 px-4 py-2.5 text-sm text-rose-400 hover:bg-rose-500/10 hover:text-rose-300 transition-colors"
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path><polyline points="16 17 21 12 16 7"></polyline><line x1="21" y1="12" x2="9" y2="12"></line></svg>
                      Logout
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
          
          {user?.role === 'super_tenant' ? (
            <div className="flex items-center gap-2 justify-center pb-1 pt-1">
              <select
                value={user.impersonatedTenantEmail || 'hub'}
                onChange={(e) => handleTenantChange(e.target.value)}
                className="bg-slate-900/60 border border-slate-800 rounded-lg text-[10px] font-bold text-white uppercase tracking-wider focus:outline-none cursor-pointer px-3 py-1 outline-none"
              >
                <option value="hub">🚀 Super Tenant Hub</option>
                {managedTenants.map(t => (
                  <option key={t.tenantId} value={t.tenantId}>
                    🏫 {t.schoolName || t.adminName}
                  </option>
                ))}
              </select>
            </div>
          ) : (
            <div className="flex items-center gap-2 justify-center pb-1 pt-1 opacity-90">
               <span className="text-[10px] font-semibold text-slate-500 uppercase">{roleDisplay}:</span>
               <span className="text-xs font-bold text-sky-400 uppercase tracking-widest">{user?.name || instituteId}</span>
            </div>
          )}

          {/* New Mobile Banner */}
          {(deferredPrompt || showIOSInstallButton) && !isStandalone && (
            <div className="mt-2 animate-in slide-in-from-top-2 duration-300">
              <button
                onClick={handleInstallClick}
                className="w-full flex items-center justify-center gap-2 bg-gradient-to-r from-sky-600 to-indigo-600 text-white rounded-lg py-2 text-[11px] font-bold tracking-widest uppercase shadow-lg shadow-sky-900/40 active:scale-[0.98] transition-transform"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                {isIOS ? 'Install on iPhone' : 'Install App'}
              </button>
            </div>
          )}
        </header>

        {/* Desktop Top Header */}
        <header className="hidden sm:flex h-16 items-center justify-between px-8 border-b border-slate-800/50 bg-slate-950/80 backdrop-blur sticky top-0 z-30">
          {user?.role === 'super_tenant' ? (
            <div className="flex items-center gap-3 bg-indigo-500/10 px-4 py-1.5 rounded-full border border-indigo-500/20 shadow-inner">
              <span className="text-[10px] font-bold text-indigo-400 uppercase tracking-widest">Active Workspace</span>
              <div className="w-px h-3 bg-indigo-500/30"></div>
              <select
                value={user.impersonatedTenantEmail || 'hub'}
                onChange={(e) => handleTenantChange(e.target.value)}
                className="bg-transparent text-xs font-bold text-slate-200 uppercase tracking-wider focus:outline-none cursor-pointer border-none py-0.5 outline-none"
              >
                <option value="hub" className="bg-slate-950 text-slate-200">🚀 Super Tenant Hub</option>
                {managedTenants.map(t => (
                  <option key={t.tenantId} value={t.tenantId} className="bg-slate-950 text-slate-200">
                    🏫 {t.schoolName || t.adminName}
                  </option>
                ))}
              </select>
            </div>
          ) : (
            <div className="flex items-center gap-3 bg-slate-900/40 px-5 py-1.5 rounded-full border border-slate-800 shadow-inner">
               <span className="text-[10px] font-semibold text-slate-500 uppercase tracking-widest">{roleDisplay}</span>
               <div className="w-px h-3 bg-slate-700"></div>
               <span className="text-xs font-bold text-sky-400 tracking-widest uppercase">{user?.name || instituteId}</span>
            </div>
          )}

          <div className="flex items-center gap-5">
            {(deferredPrompt || showIOSInstallButton) && !isStandalone && (
              <button
                onClick={handleInstallClick}
                className="flex items-center gap-2 px-4 py-1.5 rounded-full bg-gradient-to-r from-sky-600 to-indigo-600 text-white text-xs font-bold hover:shadow-lg hover:shadow-sky-500/20 transition-all animate-pulse shadow-md active:scale-95"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                {isIOS ? 'INSTALL ON IPHONE' : 'INSTALL APP'}
              </button>
            )}

            <button className="text-slate-400 hover:text-white relative">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"></path><path d="M13.73 21a2 2 0 0 1-3.46 0"></path></svg>
              <div className="absolute top-0 right-0 w-2 h-2 bg-rose-500 rounded-full"></div>
            </button>

            {/* Profile Dropdown */}
            <div className="relative">
              <button
                onClick={() => setShowProfileMenu(v => !v)}
                className="h-8 w-8 rounded-full bg-gradient-to-br from-indigo-500 to-purple-500 border border-slate-700 flex items-center justify-center text-white font-bold text-sm focus:outline-none hover:opacity-90 transition-opacity"
                title={user?.name || user?.email || 'Profile'}
              >
                {(user?.name || user?.email || 'U').charAt(0).toUpperCase()}
              </button>

              {showProfileMenu && (
                <>
                  {/* Backdrop */}
                  <div className="fixed inset-0 z-40" onClick={() => setShowProfileMenu(false)} />
                  {/* Menu */}
                  <div className="absolute right-0 top-10 z-50 w-52 bg-slate-900 border border-slate-700 rounded-xl shadow-2xl py-1 animate-in fade-in slide-in-from-top-2 duration-150">
                    <div className="px-4 py-2.5 border-b border-slate-800">
                      <p className="text-xs font-semibold text-slate-200 truncate">{user?.name || 'User'}</p>
                      <p className="text-xs text-slate-500 truncate">{user?.email}</p>
                    </div>
                    {user?.role?.toLowerCase() === 'student' && (
                      <>
                        <button
                          onClick={() => { setShowProfileMenu(false); navigate(`/${instituteId}/lms/profile`); }}
                          className="flex w-full items-center gap-3 px-4 py-2.5 text-sm text-slate-300 hover:bg-slate-800 hover:text-white transition-colors"
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path><circle cx="12" cy="7" r="4"></circle></svg>
                          Edit Profile
                        </button>
                        <button
                          onClick={() => { setShowProfileMenu(false); navigate(`/${instituteId}/lms/activation-videos`); }}
                          className="flex w-full items-center gap-3 px-4 py-2.5 text-sm text-slate-300 hover:bg-slate-800 hover:text-white transition-colors"
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m22 8-6 4 6 4V8Z"/><rect width="14" height="12" x="2" y="6" rx="2" ry="2"/></svg>
                          Activation Videos
                        </button>
                      </>
                    )}
                    {(deferredPrompt || showIOSInstallButton) && (
                      <button
                        onClick={handleInstallClick}
                        className="flex w-full items-center gap-3 px-4 py-2.5 text-sm text-sky-400 hover:bg-sky-500/10 hover:text-sky-300 transition-colors font-semibold"
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                        Install App
                      </button>
                    )}
                    <button
                      onClick={() => { setShowProfileMenu(false); navigate(`/${instituteId}/lms/pensense-dashboard?changePassword=true`); }}
                      className="flex w-full items-center gap-3 px-4 py-2.5 text-sm text-slate-300 hover:bg-slate-800 hover:text-white transition-colors"
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect><path d="M7 11V7a5 5 0 0 1 10 0v4"></path></svg>
                      Change Password
                    </button>
                    <button
                      onClick={handleLogout}
                      className="flex w-full items-center gap-3 px-4 py-2.5 text-sm text-rose-400 hover:bg-rose-500/10 hover:text-rose-300 transition-colors"
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path><polyline points="16 17 21 12 16 7"></polyline><line x1="21" y1="12" x2="9" y2="12"></line></svg>
                      Logout
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </header>

        {/* Main Content Area */}
        <main className="flex-1 min-w-0 p-4 sm:p-8 overflow-y-auto">
          <Outlet />
        </main>
      </div>

      {/* iOS Install Instructions Modal */}
      {showIOSInstructions && (
        <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center p-4 bg-slate-950/80 backdrop-blur-sm animate-in fade-in duration-300">
          <div className="fixed inset-0" onClick={() => setShowIOSInstructions(false)} />
          <div className="relative w-full max-w-sm bg-slate-900 border border-slate-800 rounded-3xl shadow-2xl overflow-hidden animate-in slide-in-from-bottom-8 duration-500">
            <div className="p-6 text-center">
              <div className="w-16 h-16 bg-gradient-to-br from-sky-500 to-indigo-600 rounded-2xl mx-auto mb-4 flex items-center justify-center shadow-lg shadow-sky-500/20">
                 <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
              </div>
              <h3 className="text-xl font-bold text-white mb-2">Install PenSense</h3>
              <p className="text-slate-400 text-sm mb-8 leading-relaxed">
                Add this app to your home screen for the best experience and quick access.
              </p>

              <div className="space-y-6 text-left">
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 rounded-full bg-slate-800 flex items-center justify-center shrink-0 text-sky-400 font-bold border border-slate-700">1</div>
                  <div className="flex-1 pt-1">
                    <p className="text-sm text-slate-200">Tap the <span className="inline-flex items-center justify-center p-1 bg-slate-800 rounded mx-1 shadow-sm"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#38bdf8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8"/><polyline points="16 6 12 2 8 6"/><line x1="12" y1="2" x2="12" y2="15"/></svg></span> **Share** button in the Safari menu bar.</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 rounded-full bg-slate-800 flex items-center justify-center shrink-0 text-sky-400 font-bold border border-slate-700">2</div>
                  <div className="flex-1 pt-1">
                    <p className="text-sm text-slate-200">Scroll down and select **"Add to Home Screen"** <span className="inline-flex items-center justify-center p-1 bg-slate-800 rounded mx-1 shadow-sm"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"/><line x1="12" y1="8" x2="12" y2="16"/><line x1="8" y1="12" x2="16" y2="12"/></svg></span></p>
                  </div>
                </div>
              </div>

              <button 
                onClick={() => setShowIOSInstructions(false)}
                className="w-full mt-10 py-3 bg-slate-800 hover:bg-slate-700 text-slate-200 font-bold rounded-xl transition-colors border border-slate-700 active:scale-95"
              >
                GOT IT
              </button>
            </div>
            
            {/* Bottom arrow visual cue for mobile */}
            <div className="sm:hidden flex justify-center pb-2 animate-bounce">
               <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#38bdf8" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M12 5v14M19 12l-7 7-7-7"/></svg>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
